import { useRef, useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Save,
  Share2,
  Download,
  Trash2,
  Pencil,
  ExternalLink,
} from "lucide-react";
import { GraphCanvas } from "@/components/Graph/GraphCanvas";
import { exportGraphPng } from "@/components/Graph/exportPng";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  getSession,
  saveSession,
  renameSession,
  deleteSession,
  createShareLink,
} from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { useUIStore } from "@/stores/uiStore";
import { toast } from "@/hooks/useToast";
import { formatDate, formatDistanceToNow } from "@/lib/dateUtils";
import type { Node } from "@/gen/tabgraph/v1/graph_pb";

export function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isSignedIn } = useAuthStore();
  const {
    selectedNodeId,
    setSelectedNodeId,
    graphLayout,
    setGraphLayout,
    isPanelOpen,
    setPanelOpen,
    subtreeFilterEnabled,
    setSubtreeFilterEnabled,
  } = useUIStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const {
    data: graph,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["session", id],
    queryFn: () => getSession(id!),
    enabled: isSignedIn && !!id,
  });

  // Handle ?nodeId= pre-selection from search results
  useEffect(() => {
    const nodeId = searchParams.get("nodeId");
    if (nodeId) setSelectedNodeId(nodeId);
  }, [searchParams, setSelectedNodeId]);

  const saveMutation = useMutation({
    mutationFn: () => saveSession(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["session", id] });
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
      toast({ title: "Session saved" });
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const renameMutation = useMutation({
    mutationFn: (name: string) => renameSession(id!, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["session", id] });
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
      setIsRenaming(false);
      toast({ title: "Session renamed" });
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Rename failed",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSession(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
      void navigate("/sessions");
      toast({ title: "Session deleted" });
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const shareMutation = useMutation({
    mutationFn: () => createShareLink(id!),
    onSuccess: ({ rawToken }) => {
      const url = `${window.location.origin}/share/${rawToken}`;
      setShareUrl(url);
      setShareDialogOpen(true);
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Share failed",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  function startRename() {
    setRenameValue(graph?.session?.name ?? "");
    setIsRenaming(true);
  }

  function submitRename(e: React.FormEvent) {
    e.preventDefault();
    if (renameValue.trim()) {
      renameMutation.mutate(renameValue.trim());
    }
  }

  function handleExportPng() {
    if (!containerRef.current) return;
    void exportGraphPng(containerRef.current, graph?.session?.name ?? "session");
  }

  const selectedNode: Node | undefined = selectedNodeId
    ? graph?.nodes.find((n) => n.id === selectedNodeId)
    : undefined;

  if (isLoading) {
    return (
      <div className="flex flex-col h-[calc(100vh-3.5rem)]">
        <div className="p-4 border-b flex items-center gap-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-8 w-20" />
        </div>
        <Skeleton className="flex-1" />
      </div>
    );
  }

  if (error || !graph) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-destructive">
          {error instanceof Error ? error.message : "Session not found."}
        </p>
      </div>
    );
  }

  const session = graph.session;
  const isSaved = !!session?.savedAt;

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 3.5rem)" }}>
      {/* Toolbar */}
      <div className="border-b bg-background px-4 py-2 flex items-center gap-3 flex-wrap">
        {isRenaming ? (
          <form
            onSubmit={submitRename}
            className="flex items-center gap-2"
          >
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className="h-8 w-48"
              autoFocus
              onBlur={() => setIsRenaming(false)}
            />
            <Button type="submit" size="sm" disabled={renameMutation.isPending}>
              Save
            </Button>
          </form>
        ) : (
          <div className="flex items-center gap-2">
            <span className="font-semibold">
              {session?.name || "Untitled session"}
            </span>
            {isSaved && <Badge variant="secondary">Saved</Badge>}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={startRename}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          {/* Subtree filter toggle — only shown for large graphs */}
          {graph.nodes.length > 500 && (
            <Tabs
              value={subtreeFilterEnabled ? "subtree" : "all"}
              onValueChange={(v) =>
                setSubtreeFilterEnabled(v === "subtree")
              }
            >
              <TabsList className="h-8">
                <TabsTrigger value="subtree" className="text-xs h-7 px-2">
                  Limit to subtree
                </TabsTrigger>
                <TabsTrigger value="all" className="text-xs h-7 px-2">
                  Show all
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Layout toggle */}
          <Tabs
            value={graphLayout}
            onValueChange={(v) =>
              setGraphLayout(v as "hierarchical" | "force")
            }
          >
            <TabsList className="h-8">
              <TabsTrigger value="hierarchical" className="text-xs h-7 px-2">
                Hierarchical
              </TabsTrigger>
              <TabsTrigger value="force" className="text-xs h-7 px-2">
                Force
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {!isSaved && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              Save
            </Button>
          )}

          <Button
            size="sm"
            variant="outline"
            onClick={() => shareMutation.mutate()}
            disabled={shareMutation.isPending}
          >
            <Share2 className="h-3.5 w-3.5 mr-1" />
            Share
          </Button>

          <Button size="sm" variant="outline" onClick={handleExportPng}>
            <Download className="h-3.5 w-3.5 mr-1" />
            Export PNG
          </Button>

          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Graph area */}
      <div className="flex-1 overflow-hidden relative" ref={containerRef}>
        <GraphCanvas graph={graph} className="w-full h-full" />
      </div>

      {/* Node detail Sheet */}
      <Sheet
        open={isPanelOpen}
        onOpenChange={(open) => {
          setPanelOpen(open);
          if (!open) setSelectedNodeId(null);
        }}
      >
        <SheetContent className="overflow-y-auto">
          {selectedNode ? (
            <>
              <SheetHeader>
                <SheetTitle className="line-clamp-2 pr-6">
                  {selectedNode.page?.title || selectedNode.canonicalUrl}
                </SheetTitle>
                <SheetDescription className="break-all">
                  {selectedNode.urlHost}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-4 space-y-4">
                {selectedNode.page?.faviconUrl && (
                  <img
                    src={selectedNode.page.faviconUrl}
                    alt=""
                    className="w-8 h-8 rounded"
                    onError={(e) =>
                      ((e.target as HTMLImageElement).style.display = "none")
                    }
                  />
                )}

                <div>
                  <p className="text-xs text-muted-foreground mb-1">URL</p>
                  <p className="text-sm break-all">{selectedNode.canonicalUrl}</p>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Visits</p>
                    <p className="font-medium">{selectedNode.visitCount}</p>
                  </div>
                  {selectedNode.firstSeenAt && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        First seen
                      </p>
                      <p className="font-medium">
                        {formatDistanceToNow(
                          new Date(
                            Number(selectedNode.firstSeenAt.seconds) * 1000,
                          ),
                        )}
                      </p>
                    </div>
                  )}
                  {selectedNode.lastSeenAt && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        Last seen
                      </p>
                      <p className="font-medium">
                        {formatDate(
                          new Date(
                            Number(selectedNode.lastSeenAt.seconds) * 1000,
                          ),
                        )}
                      </p>
                    </div>
                  )}
                </div>

                <Separator />

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  asChild
                >
                  <a
                    href={selectedNode.canonicalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    Open in new tab
                  </a>
                </Button>
              </div>
            </>
          ) : (
            <SheetHeader>
              <SheetTitle>Select a node</SheetTitle>
              <SheetDescription>
                Tap any node in the graph to see its details.
              </SheetDescription>
            </SheetHeader>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete session?</DialogTitle>
            <DialogDescription>
              This will permanently delete{" "}
              <strong>{session?.name || "this session"}</strong> and all its
              graph data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteDialogOpen(false);
                deleteMutation.mutate();
              }}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share session</DialogTitle>
            <DialogDescription>
              Anyone with this link can view the graph (read-only). The link
              expires after 7 days by default.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input value={shareUrl} readOnly className="font-mono text-xs" />
            <Button
              onClick={() => {
                void navigator.clipboard.writeText(shareUrl);
                toast({ title: "Link copied to clipboard" });
              }}
            >
              Copy
            </Button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShareDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
